/**
 * Role-specific dashboard views - Executive Glass Design System
 *
 * This component provides different dashboard layouts and content
 * based on the user's role, ensuring users see the most relevant
 * information for their responsibilities.
 *
 * Uses the Executive Glass design system with:
 * - Glass morphism effects
 * - Semantic color tokens
 * - MetricCard components for KPIs
 * - SectionReveal for progressive animations
 * - GlassButton for actions
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/store';
import { fetchExecutiveDashboard, fetchProgramLeadDashboard } from '@/store/slices/dashboardSlice';
import { usePermissions } from '@/hooks/usePermissions';
import { UserRole } from '@/types';
import {
  BarChart3,
  Users,
  Target,
  TrendingUp,
  Calendar,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Layers,
  Building2,
  ClipboardList,
  Eye,
  LayoutDashboard,
} from 'lucide-react';

// Design System Components
import {
  GlassCard,
  GlassButton,
  MetricCard,
  SectionReveal,
  GlassPageHeader,
} from '@/design-system';

// Page Components
import ISOwnerDashboardPage from '@/pages/dashboard/ISOwnerDashboardPage';

/**
 * QuickAction - Redesigned with glass morphism
 *
 * A quick action card that links to different areas of the application.
 * Uses GlassCard with hover-lift effect for interactive feedback.
 */
interface QuickActionProps {
  title: string;
  description: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  iconColor?: 'cyan' | 'blue' | 'violet' | 'emerald' | 'amber' | 'rose';
}

const QuickAction: React.FC<QuickActionProps> = ({
  title,
  description,
  href,
  icon: Icon,
  iconColor = 'cyan',
}) => {
  const iconColorMap = {
    cyan: 'bg-accent-cyan-soft text-accent-cyan',
    blue: 'bg-accent-blue-soft text-accent-blue',
    violet: 'bg-accent-violet-soft text-accent-violet',
    emerald: 'bg-accent-emerald-soft text-accent-emerald',
    amber: 'bg-accent-amber-soft text-accent-amber',
    rose: 'bg-accent-rose-soft text-accent-rose',
  };

  return (
    <Link to={href}>
      <GlassCard variant="default" padding="md" hover className="h-full">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconColorMap[iconColor]}`}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-medium text-foreground">{title}</h3>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
      </GlassCard>
    </Link>
  );
};

/**
 * EmptyStateCard - Consistent empty state display
 *
 * Shows a friendly message when no data is available,
 * with a call-to-action button.
 */
interface EmptyStateCardProps {
  title: string;
  message: string;
  actionLabel?: string;
  actionHref?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

const EmptyStateCard: React.FC<EmptyStateCardProps> = ({
  title,
  message,
  actionLabel,
  actionHref,
  icon: Icon = LayoutDashboard,
}) => (
  <GlassCard variant="default" padding="lg">
    <div className="text-center py-8">
      <Icon className="w-16 h-16 mx-auto mb-4 text-muted-foreground/60" />
      <div className="text-muted-foreground text-lg mb-2">{title}</div>
      <p className="text-muted-foreground/60 mb-6 max-w-md mx-auto">{message}</p>
      {actionLabel && actionHref && (
        <Link to={actionHref}>
          <GlassButton variant="primary" size="md">
            {actionLabel}
          </GlassButton>
        </Link>
      )}
    </div>
  </GlassCard>
);

/**
 * LoadingStateCard - Consistent loading state display
 */
const LoadingStateCard: React.FC<{ message?: string }> = ({
  message = 'Loading your dashboard...',
}) => (
  <GlassCard variant="default" padding="lg">
    <div className="text-center py-8">
      <div className="animate-pulse">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-bg-tertiary" />
        <div className="h-6 w-48 mx-auto mb-2 rounded bg-bg-tertiary" />
        <div className="h-4 w-64 mx-auto rounded bg-bg-tertiary" />
      </div>
      <p className="text-muted-foreground mt-4">{message}</p>
    </div>
  </GlassCard>
);

/**
 * DashboardHeader - Consistent header for all dashboard views
 */
interface DashboardHeaderProps {
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  iconGradient?: 'blue-violet' | 'emerald' | 'amber' | 'rose' | 'cyan';
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  title,
  subtitle,
  icon: Icon,
  iconGradient = 'blue-violet',
}) => (
  <GlassPageHeader
    title={title}
    subtitle={subtitle}
    icon={<Icon className="w-6 h-6" />}
    iconGradient={iconGradient}
    className="mb-6"
  />
);

/**
 * QuickActionsSection - Consistent quick actions section
 */
interface QuickActionsSectionProps {
  title?: string;
  children: React.ReactNode;
}

const QuickActionsSection: React.FC<QuickActionsSectionProps> = ({
  title = 'Quick Actions',
  children,
}) => (
  <div>
    <h2 className="text-lg font-semibold text-foreground mb-4">{title}</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {children}
    </div>
  </div>
);

/**
 * RoleBasedDashboard - Main component
 *
 * Renders different dashboard views based on user role:
 * - EXECUTIVE: Portfolio overview with strategic insights
 * - IS_OWNER: Investible structure management
 * - PROGRAM_LEAD: Delivery capacity and team allocations
 * - PMO: Full planning workflow management
 * - FINANCE: Financial oversight and cost management
 * - VIEWER: Read-only portfolio access
 */
export const RoleBasedDashboard: React.FC = () => {
  const { role } = usePermissions();
  const dispatch = useDispatch<AppDispatch>();
  const { executive, programLead } = useSelector((state: RootState) => state.dashboard);

  // Fetch dashboard data on mount - role-specific
  useEffect(() => {
    if (role === UserRole.EXECUTIVE || role === UserRole.PMO || role === UserRole.ADMIN) {
      dispatch(fetchExecutiveDashboard());
    } else if (role === UserRole.PROGRAM_LEAD) {
      dispatch(fetchProgramLeadDashboard());
    }
  }, [dispatch, role]);

  // =========================================================================
  // EXECUTIVE DASHBOARD
  // =========================================================================
  if (role === UserRole.EXECUTIVE) {
    return (
      <div className="glass-page-bg min-h-screen p-6 lg:p-8">
        <div className="space-y-6">
          <SectionReveal delay={0}>
            <DashboardHeader
              title="Executive Dashboard"
              subtitle="Portfolio-level insights and strategic overview"
              icon={BarChart3}
              iconGradient="blue-violet"
            />
          </SectionReveal>

          <SectionReveal delay={0.1}>
            <EmptyStateCard
              title="No Data Available"
              message="No planning data found. Contact your PMO team to set up planning periods and portfolio data."
              actionLabel="View Executive Dashboard"
              actionHref="/reports/executive"
              icon={BarChart3}
            />
          </SectionReveal>

          <SectionReveal delay={0.2}>
            <QuickActionsSection>
              <QuickAction
                title="Review Portfolio"
                description="View detailed executive dashboard"
                href="/reports/executive"
                icon={BarChart3}
                iconColor="violet"
              />
              <QuickAction
                title="Approve EPICs"
                description="Review and approve submitted EPICs"
                href="/epics"
                icon={CheckCircle}
                iconColor="emerald"
              />
              <QuickAction
                title="View Scenarios"
                description="Compare planning scenarios"
                href="/scenarios"
                icon={Layers}
                iconColor="blue"
              />
            </QuickActionsSection>
          </SectionReveal>
        </div>
      </div>
    );
  }

  // =========================================================================
  // IS OWNER DASHBOARD
  // Delegates to the full ISOwnerDashboardPage component which handles
  // data fetching, IS auto-selection, and comprehensive dashboard rendering
  // =========================================================================
  if (role === UserRole.IS_OWNER) {
    return <ISOwnerDashboardPage />;
  }

  // =========================================================================
  // PROGRAM LEAD DASHBOARD
  // =========================================================================
  if (role === UserRole.PROGRAM_LEAD) {
    // Get real data from Redux
    const epicData = programLead.epicAccountability as any;
    const teamCount = programLead.capacityUtilization?.teamCount ?? 0;
    const primaryEpics = epicData?.primaryEpics?.length ?? 0;
    const contributingEpics = epicData?.contributingEpics?.length ?? 0;
    const totalEpics = primaryEpics + contributingEpics;
    const avgUtilization = programLead.capacityUtilization?.utilizationPercentage ?? 0;

    // Calculate at-risk EPICs (utilization < 80% or > 110%)
    const allEpics = [
      ...(epicData?.primaryEpics ?? []),
      ...(epicData?.contributingEpics ?? []),
    ];
    const atRiskEpics = allEpics.filter(
      (epic: any) => epic.utilizationPercentage < 80 || epic.utilizationPercentage > 110
    ).length;

    const hasData = teamCount > 0 || totalEpics > 0;

    // Determine utilization color
    const getUtilizationColor = (): 'rose' | 'amber' | 'emerald' => {
      if (avgUtilization > 100) return 'rose';
      if (avgUtilization > 90) return 'amber';
      return 'emerald';
    };

    return (
      <div className="glass-page-bg min-h-screen p-6 lg:p-8">
        <div className="space-y-6">
          <SectionReveal delay={0}>
            <DashboardHeader
              title="Program Lead Dashboard"
              subtitle="Manage delivery capacity and team allocations"
              icon={Users}
              iconGradient="emerald"
            />
          </SectionReveal>

          {/* Loading State */}
          {programLead.isLoading && (
            <SectionReveal delay={0.1}>
              <LoadingStateCard message="Fetching your program data" />
            </SectionReveal>
          )}

          {/* Empty State */}
          {!hasData && !programLead.isLoading && (
            <SectionReveal delay={0.1}>
              <EmptyStateCard
                title="No Data Available"
                message="No programs or teams assigned. Contact your PMO team to set up programs and assign teams."
                actionLabel="View Programs"
                actionHref="/programs"
                icon={Users}
              />
            </SectionReveal>
          )}

          {/* KPI Cards */}
          {hasData && !programLead.isLoading && (
            <SectionReveal delay={0.1}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Link to="/teams">
                  <MetricCard
                    title="Teams"
                    value={teamCount}
                    subtitle="Active teams in program"
                    icon={<Users className="w-5 h-5" />}
                    iconColor="cyan"
                  />
                </Link>
                <Link to="/epics">
                  <MetricCard
                    title="Total EPICs"
                    value={totalEpics}
                    subtitle={`${primaryEpics} primary`}
                    icon={<Target className="w-5 h-5" />}
                    iconColor="blue"
                  />
                </Link>
                <Link to="/epics">
                  <MetricCard
                    title="At Risk EPICs"
                    value={atRiskEpics}
                    subtitle="Require attention"
                    icon={<AlertTriangle className="w-5 h-5" />}
                    iconColor="rose"
                  />
                </Link>
                <Link to="/capacity/programs">
                  <MetricCard
                    title="Capacity Utilization"
                    value={`${Math.round(avgUtilization)}%`}
                    subtitle="Average across teams"
                    icon={<TrendingUp className="w-5 h-5" />}
                    iconColor={getUtilizationColor()}
                  />
                </Link>
              </div>
            </SectionReveal>
          )}

          <SectionReveal delay={0.2}>
            <QuickActionsSection>
              <QuickAction
                title="Manage Allocations"
                description="Update team capacity allocations"
                href="/allocations"
                icon={Users}
                iconColor="cyan"
              />
              <QuickAction
                title="Update Delivery Status"
                description="Report on feature progress"
                href="/features"
                icon={CheckCircle}
                iconColor="emerald"
              />
              <QuickAction
                title="Program Dashboard"
                description="Detailed program analytics"
                href="/reports/program-lead"
                icon={BarChart3}
                iconColor="blue"
              />
            </QuickActionsSection>
          </SectionReveal>
        </div>
      </div>
    );
  }

  // =========================================================================
  // PMO DASHBOARD
  // =========================================================================
  if (role === UserRole.PMO) {
    // Get real data from Redux
    const totalEpics = executive.kpis?.activeEpics?.total ?? 0;
    const onTrackEpics = executive.kpis?.activeEpics?.onTrack ?? 0;
    const atRiskEpics = executive.kpis?.activeEpics?.atRisk ?? 0;
    const hasData = totalEpics > 0;

    return (
      <div className="glass-page-bg min-h-screen p-6 lg:p-8">
        <div className="space-y-6">
          <SectionReveal delay={0}>
            <DashboardHeader
              title="PMO Dashboard"
              subtitle="Full planning workflow management and oversight"
              icon={ClipboardList}
              iconGradient="amber"
            />
          </SectionReveal>

          {/* Empty State */}
          {!hasData && !executive.isLoading && (
            <SectionReveal delay={0.1}>
              <EmptyStateCard
                title="No Data Available"
                message="No planning periods or EPICs found. Start by creating a planning period and adding investible structures."
                actionLabel="Create Planning Period"
                actionHref="/planning-periods"
                icon={Calendar}
              />
            </SectionReveal>
          )}

          {/* KPI Cards */}
          {hasData && (
            <SectionReveal delay={0.1}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Link to="/planning-periods">
                  <MetricCard
                    title="Planning Status"
                    value={hasData ? 'Active' : 'No Data'}
                    subtitle={hasData ? 'Planning in progress' : 'Create a planning period'}
                    icon={<Calendar className="w-5 h-5" />}
                    iconColor="violet"
                  />
                </Link>
                <Link to="/epics">
                  <MetricCard
                    title="Total EPICs"
                    value={totalEpics.toString()}
                    subtitle={`${onTrackEpics} On Track, ${atRiskEpics} At Risk`}
                    icon={<Target className="w-5 h-5" />}
                    iconColor="blue"
                  />
                </Link>
                <MetricCard
                  title="Workflow Health"
                  value={hasData ? 'N/A' : 'No Data'}
                  subtitle="Process compliance"
                  icon={<CheckCircle className="w-5 h-5" />}
                  iconColor="emerald"
                />
                <Link to="/allocations">
                  <MetricCard
                    title="Resource Utilization"
                    value={hasData ? 'N/A' : 'No Data'}
                    subtitle="Across all programs"
                    icon={<Users className="w-5 h-5" />}
                    iconColor="cyan"
                  />
                </Link>
              </div>
            </SectionReveal>
          )}

          <SectionReveal delay={0.2}>
            <QuickActionsSection>
              <QuickAction
                title="Manage Planning"
                description="Configure planning periods"
                href="/planning-periods"
                icon={Calendar}
                iconColor="violet"
              />
              <QuickAction
                title="Review EPICs"
                description="Prioritize and approve EPICs"
                href="/epics"
                icon={Target}
                iconColor="blue"
              />
              <QuickAction
                title="Capacity Planning"
                description="Optimize resource allocation"
                href="/allocations"
                icon={Users}
                iconColor="cyan"
              />
              <QuickAction
                title="User Management"
                description="Manage system users"
                href="/users"
                icon={Users}
                iconColor="emerald"
              />
              <QuickAction
                title="Generate Reports"
                description="Export planning data"
                href="/reports/executive"
                icon={BarChart3}
                iconColor="amber"
              />
              <QuickAction
                title="Workflow Automation"
                description="Configure business rules"
                href="/scenarios"
                icon={Layers}
                iconColor="rose"
              />
            </QuickActionsSection>
          </SectionReveal>
        </div>
      </div>
    );
  }

  // =========================================================================
  // FINANCE DASHBOARD
  // =========================================================================
  if (role === UserRole.FINANCE) {
    return (
      <div className="glass-page-bg min-h-screen p-6 lg:p-8">
        <div className="space-y-6">
          <SectionReveal delay={0}>
            <DashboardHeader
              title="Finance Dashboard"
              subtitle="Financial oversight and cost management"
              icon={DollarSign}
              iconGradient="amber"
            />
          </SectionReveal>

          <SectionReveal delay={0.1}>
            <EmptyStateCard
              title="No Data Available"
              message="No financial data found. Data will appear once programs, teams, and allocations are configured."
              actionLabel="View Executive Dashboard"
              actionHref="/reports/executive"
              icon={DollarSign}
            />
          </SectionReveal>

          <SectionReveal delay={0.2}>
            <QuickActionsSection>
              <QuickAction
                title="Financial Reports"
                description="View cost and budget analysis"
                href="/reports/executive"
                icon={BarChart3}
                iconColor="amber"
              />
              <QuickAction
                title="Export Data"
                description="Download financial data"
                href="/reports/executive"
                icon={DollarSign}
                iconColor="emerald"
              />
              <QuickAction
                title="View Allocations"
                description="Review capacity investments"
                href="/allocations"
                icon={Users}
                iconColor="blue"
              />
            </QuickActionsSection>
          </SectionReveal>
        </div>
      </div>
    );
  }

  // =========================================================================
  // VIEWER DASHBOARD
  // =========================================================================
  if (role === UserRole.VIEWER) {
    return (
      <div className="glass-page-bg min-h-screen p-6 lg:p-8">
        <div className="space-y-6">
          <SectionReveal delay={0}>
            <DashboardHeader
              title="Portfolio Overview"
              subtitle="Read-only access to portfolio information"
              icon={Eye}
              iconGradient="cyan"
            />
          </SectionReveal>

          <SectionReveal delay={0.1}>
            <EmptyStateCard
              title="No Data Available"
              message="No portfolio data found. Contact your PMO team for access to planning data."
              icon={Eye}
            />
          </SectionReveal>

          <SectionReveal delay={0.2}>
            <QuickActionsSection title="Available Views">
              <QuickAction
                title="View EPICs"
                description="Browse portfolio initiatives"
                href="/epics"
                icon={Target}
                iconColor="cyan"
              />
              <QuickAction
                title="View Features"
                description="See feature development"
                href="/features"
                icon={CheckCircle}
                iconColor="emerald"
              />
              <QuickAction
                title="View Programs"
                description="Browse delivery programs"
                href="/programs"
                icon={Users}
                iconColor="blue"
              />
            </QuickActionsSection>
          </SectionReveal>
        </div>
      </div>
    );
  }

  // =========================================================================
  // DEFAULT DASHBOARD (Fallback)
  // =========================================================================
  return (
    <div className="glass-page-bg min-h-screen p-6 lg:p-8">
      <div className="space-y-6">
        <SectionReveal delay={0}>
          <DashboardHeader
            title="Dashboard"
            subtitle="Welcome to Portfolio Management"
            icon={LayoutDashboard}
            iconGradient="blue-violet"
          />
        </SectionReveal>

        <SectionReveal delay={0.1}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <QuickAction
              title="View Portfolio"
              description="Browse available information"
              href="/epics"
              icon={Target}
              iconColor="cyan"
            />
            <QuickAction
              title="User Profile"
              description="Manage your profile"
              href="/profile"
              icon={Users}
              iconColor="blue"
            />
          </div>
        </SectionReveal>
      </div>
    </div>
  );
};
